import * as React from 'react';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type InputSetter = (v: any) => void;

type InputType = 'text' | 'checkbox';

type ValueType<T extends InputType> = T extends 'checkbox' ? boolean : string;

const INPUT_SETTER_MAP: Record<InputType, string> = {
  text: 'value',
  checkbox: 'checked',
};

const INPUT_EVENT_MAP: Record<InputType, string> = {
  text: 'input',
  checkbox: 'click',
};

export const useNativeInput = <T extends InputType>(
  name: string,
  inputType: T,
) => {
  const inputElementRef = React.useRef<HTMLInputElement | null>(null);
  const inputValueSetterRef = React.useRef<InputSetter | undefined>(undefined);

  const setValue = React.useCallback(
    (value: ValueType<T>) => {
      if (inputElementRef.current && inputValueSetterRef.current) {
        inputValueSetterRef.current.call(inputElementRef.current, value);
        const ev = new Event(INPUT_EVENT_MAP[inputType], { bubbles: true });
        inputElementRef.current.dispatchEvent(ev);
      }
    },
    [inputType],
  );

  React.useEffect(() => {
    const inputList = document.getElementsByName(name);

    if (inputList.length > 0) {
      const nativeInput = inputList[0] as HTMLInputElement;

      inputElementRef.current = nativeInput;

      inputValueSetterRef.current = Object.getOwnPropertyDescriptor(
        window.HTMLInputElement.prototype,
        INPUT_SETTER_MAP[inputType],
      )?.set;
    }
  }, [name, inputType]);

  return { setNativeValue: setValue };
};
